import {
  Alert,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import { DoraButton, UFDoraSelect, UFDoraTextInput, UFDoraDatePicker } from "dora-shared/frontend";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DateTime } from "luxon";
import PlaceSearchWithSavedLocations from "../../PlaceSearchWithSavedLocations/PlaceSearchWithSavedLocations";
import { AddressChange } from "../../NewPlaceSearch";
import { StopPlace } from "../../../ducks/app/cargo-and-template-shared";
import { useEffect, useCallback, useMemo, useState } from "react";
import { useAppDispatch, useSelector } from "../../../redux-store";
import {
  clearSplitCargos,
  loadCargosForSplit,
  selectSplitCargos,
  selectSplitCargosLoading,
  splitCargo,
  splitRoute,
} from "../../../ducks/data/split-cargo";
import { selectClient } from "../../../ducks/data/clients/selectors";
import { useDrivers } from "../../../ducks/data/drivers/hooks";
import { selectRouteViewModel } from "../../../ducks/data/route-views/selectors";
import { selectTrailers } from "../../../ducks/app/fleet-management/selectors";
import { loadTrailers } from "../../../ducks/data/trailers";
import { SplitRouteNewRouteOption } from "dora-contracts";
import { useTranslation } from "react-i18next";
import { selectTeams } from "../../../ducks/data/teams/selectors";

interface SplitCargoFormData {
  place: StopPlace | null;
  reloadDate: DateTime | null;
  selectedCargosIds: string[];
  newRouteOption: SplitRouteNewRouteOption;
  truckId: string | null;
  trailerId: string | null;
  driverId: string | null;
  routeName: string | null;
  teamId: string | null;
}

type LoadedCargo = ReturnType<typeof selectSplitCargos>[number];

const buildSchema = (cargos: LoadedCargo[], reloadDateError: string) =>
  yup.object({
    place: yup.object().required(),
    selectedCargosIds: yup.array(yup.string().required()).min(1).required(),
    reloadDate: yup
      .mixed<DateTime>()
      .required()
      .test("reload-date-between-stop-dates", reloadDateError, function (date) {
        if (!date) return true;
        const { selectedCargosIds } = this.parent as SplitCargoFormData;
        if (!selectedCargosIds?.length) return true;
        const selected = cargos.filter((c) => selectedCargosIds.includes(c.id));
        const reloadDay = date.startOf("day");
        return selected.every((c) => {
          if (!c.pickupList.length || !c.dropoffList.length) return false;
          const pickupDates = c.pickupList
            .map((s) => DateTime.fromFormat(s.date, "yyyy-MM-dd"))
            .filter((d) => d.isValid);
          const dropoffDates = c.dropoffList
            .map((s) => DateTime.fromFormat(s.date, "yyyy-MM-dd"))
            .filter((d) => d.isValid);
          if (!pickupDates.length || !dropoffDates.length) return false;
          // Mirrors the backend: the reload point becomes fragment 1's dropoff
          // (must not precede any real pickup) and fragment 2's pickup (must
          // not follow any real dropoff) - so it must be >= the latest pickup
          // and <= the earliest dropoff, not just anywhere in the overall
          // pickup/dropoff date range.
          const maxPickupDate = DateTime.max(...pickupDates)!;
          const minDropoffDate = DateTime.min(...dropoffDates)!;
          return (
            reloadDay.toMillis() >= maxPickupDate.toMillis() &&
            reloadDay.toMillis() <= minDropoffDate.toMillis()
          );
        });
      }),
  });

export default function SplitRouteOrCargoDialog({
  onClose,
  cargosIds,
  isSplitRoute = false,
  routeId = null,
}: {
  onClose: () => void;
  cargosIds: string[];
  isSplitRoute?: boolean;
  routeId: string | null;
}) {
  const dispatch = useAppDispatch();
  const cargos = useSelector(selectSplitCargos);
  const loading = useSelector(selectSplitCargosLoading);
  const trailers = useSelector(selectTrailers);
  const sourceRouteViewModel = useSelector(selectRouteViewModel(routeId ?? ""));
  const { drivers } = useDrivers();
  const teams = useSelector(selectTeams);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [splitError, setSplitError] = useState<{
    severity: "error" | "warning";
    message: string;
  } | null>(null);
  const { t } = useTranslation(["split", "components"]);

  const onDialogClose = () => {
    dispatch(clearSplitCargos());
    onClose();
  };

  const splitDateDefault = useMemo(() => {
    if (loading) {
      return null;
    }
    const allPickupsDates = cargos
      .flatMap((c) => c.pickupList)
      .map((p) => DateTime.fromFormat(p.date, "yyyy-MM-dd"));
    return DateTime.max(...allPickupsDates);
  }, [cargos, loading]);

  useEffect(() => {
    dispatch(loadTrailers());
  }, [dispatch]);

  const truckOptions = useMemo(
    () => [
      { value: null, label: t("split:splitNoneSelected") },
      ...trailers
        .filter((v) => v.powered)
        .map((v) => ({
          value: v.id,
          label: `${v.number}${v.alias ? ` (${v.alias})` : ""}`,
        })),
    ],
    [trailers, t]
  );

  const trailerOptions = useMemo(
    () => [
      { value: null, label: t("split:splitNoneSelected") },
      ...trailers
        .filter((v) => !v.powered)
        .map((v) => ({
          value: v.id,
          label: `${v.number}${v.alias ? ` (${v.alias})` : ""}`,
        })),
    ],
    [trailers, t]
  );

  const driverOptions = useMemo(
    () => [
      { value: null, label: t("split:splitNoneSelected") },
      ...drivers.map((d) => ({
        value: d.id,
        label: `${d.firstName} ${d.lastName}`,
      })),
    ],
    [drivers, t]
  );

  const teamOptions = useMemo(
    () => [
      { value: null, label: t("split:splitNoneSelected") },
      ...teams.map((t) => ({
        value: t.id,
        label: `${t.name}`,
      })),
    ],
    [teams, t]
  );

  // due to cargosIds reference changing and triggering rerenders
  const cargosIdsKey = cargosIds.join(",");

  useEffect(() => {
    dispatch(clearSplitCargos());
    dispatch(loadCargosForSplit(cargosIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargosIdsKey, dispatch]);

  const schema = useMemo(
    () => buildSchema(cargos, t("split:reloadDateBetweenDates")),
    [cargos, t]
  );

  const {
    control,
    register,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors, isValid, touchedFields },
  } = useForm<SplitCargoFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      place: null,
      reloadDate: null,
      selectedCargosIds: [],
      newRouteOption: "UNDISPATCHED",
      truckId: null,
      trailerId: null,
      driverId: null,
      teamId: null,
      routeName: null,
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!getValues("reloadDate") && splitDateDefault) {
      setValue("reloadDate", splitDateDefault, { shouldValidate: true });
    }
  }, [splitDateDefault, getValues, setValue]);

  const newRouteOption = watch("newRouteOption");

  useEffect(() => {
    if (
      newRouteOption === "NEW_ROUTE" &&
      !touchedFields.trailerId &&
      getValues("trailerId") === null &&
      sourceRouteViewModel?.unpoweredTrailerId
    ) {
      setValue("trailerId", sourceRouteViewModel.unpoweredTrailerId);
    }
  }, [newRouteOption, sourceRouteViewModel, touchedFields.trailerId, getValues, setValue]);

  const selectedCargosIds = watch("selectedCargosIds");

  useEffect(() => {
    if (cargos.length === 1) {
      setValue("selectedCargosIds", [cargos[0].id], { shouldValidate: true });
    } else if (cargos.length > 1) {
      setValue(
        "selectedCargosIds",
        cargos
          // .filter((c) => !c.hasBeenSplit && !c.splitFromCargoId)
          .map((c) => c.id),
        { shouldValidate: true }
      );
    }
  }, [cargos, setValue]);

  const toggleCargoId = useCallback(
    (id: string) => {
      const current = selectedCargosIds ?? [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      setValue("selectedCargosIds", next, { shouldValidate: true });
      void trigger("reloadDate");
    },
    [selectedCargosIds, setValue, trigger]
  );

  const onPlaceSelected = (p: null | AddressChange) => {
    const place: StopPlace | null = p
      ? {
          placeName: p.placeName || p.place || "",
          postcode: p.postcode,
          city: p.city,
          country: p.country,
          address: p.address,
          coord: p.coord,
        }
      : null;
    setValue("place", place, { shouldValidate: true });
  };

  const onSplitClick = async () => {
    const { place, reloadDate, selectedCargosIds: ids } = watch();
    if (!place || !reloadDate || !ids.length) return;
    setSplitError(null);
    setIsSubmitting(true);
    try {
      if (isSplitRoute && routeId) {
        const {
          newRouteOption,
          truckId,
          trailerId,
          driverId,
          teamId,
          routeName,
        } = watch();
        await dispatch(
          splitRoute({
            cargosIds: ids,
            routeId,
            place,
            date: reloadDate.toFormat("yyyy-MM-dd"),
            newRouteOption,
            truckId,
            trailerId,
            driverId,
            routeName,
            teamId,
          })
        ).unwrap();
      } else {
        await dispatch(
          splitCargo({
            cargoId: ids[0],
            place,
            date: reloadDate.toFormat("yyyy-MM-dd"),
          })
        ).unwrap();
      }
      onDialogClose();
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      setSplitError(
        name === "JobPollTimeoutError"
          ? { severity: "warning", message: t("split:splitTimeout") }
          : { severity: "error", message: t("split:splitFailed") }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // TODO: fix any
  const formatAddress = (place: StopPlace | null | any) => {
    if (!place) {
      return "";
    }
    const { placeName, postcode, city, country, address } = place;
    const addressParts = [placeName, postcode, city, country, address].filter(
      (x) => !!x
    );

    return addressParts.join(", ");
  };

  return (
    <Dialog open onClose={onDialogClose} onClick={(e) => e.stopPropagation()}>
      <DialogTitle>
        {cargosIds.length === 1 ? t("splitOrder") : t("splitRoute")}
      </DialogTitle>
      <DialogContent>
        <div style={{ minWidth: 500 }}>
          {splitError && (
            <Alert severity={splitError.severity} sx={{ mb: 2 }}>
              {splitError.message}
            </Alert>
          )}
          {isSubmitting ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <CircularProgress size={32} />
              <span>{t("split:processingSplit")}</span>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <CircularProgress size={32} />
              <span>{t("split:loadingOrders")}</span>
            </div>
          ) : cargos.length > 1 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{t("split:tableColumns.ref")}</TableCell>
                  <TableCell>{t("split:tableColumns.client")}</TableCell>
                  <TableCell>{t("split:tableColumns.from")}</TableCell>
                  <TableCell>{t("split:tableColumns.to")}</TableCell>
                  <TableCell>{t("split:tableColumns.status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargos.map((cargo) => (
                  <TableRow key={cargo.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCargosIds?.includes(cargo.id) ?? false}
                        onChange={() => toggleCargoId(cargo.id)}
                        // disabled={
                        //   cargo.hasBeenSplit || !!cargo.splitFromCargoId
                        // }
                      />
                    </TableCell>
                    <TableCell>{cargo.ref}</TableCell>
                    <TableCell>
                      <CargoClient clientId={cargo.clientId} />
                    </TableCell>
                    <TableCell>
                      <div>{formatAddress(cargo.pickupList[0].place)}</div>
                      <div style={{ color: "var(--gray-400)", fontSize: 12 }}>
                        {cargo.pickupList[0].date}
                      </div>
                    </TableCell>
                    <TableCell>
                      {cargo.dropoffList.length ? (
                        <>
                          <div>
                            {formatAddress(
                              cargo.dropoffList[cargo.dropoffList.length - 1]
                                .place
                            )}
                          </div>
                          <div
                            style={{ color: "var(--gray-400)", fontSize: 12 }}
                          >
                            {
                              cargo.dropoffList[cargo.dropoffList.length - 1]
                                .date
                            }
                          </div>
                        </>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {cargo.hasBeenSplit
                        ? t("split:alreadySplit")
                        : cargo.splitFromCargoId
                          ? t("split:fragmentOfAnotherCargo")
                          : t("split:notSplit")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : cargos.length === 1 ? (
            <SingleCargoDetails
              cargo={cargos[0]}
              formatAddress={formatAddress}
            />
          ) : null}
          <div className="mt-2 mb-1">
            <label>{t("split:reloadAddress")}</label>
          </div>
          <PlaceSearchWithSavedLocations
            id="split-cargo-place"
            noLabel
            onPlaceSelected={onPlaceSelected}
            placeError={!!errors.place && !!touchedFields.place}
          />
          <div className="mt-3 mb-2">
            <label>{t("split:reloadDate")}</label>
          </div>
          <div>
            <UFDoraDatePicker
              name="reloadDate"
              id={"split-cargo-reload-date"}
              control={control}
              label={t("split:reloadDate")}
              error={
                errors.reloadDate?.type === "reload-date-between-stop-dates"
                  ? errors.reloadDate
                  : undefined
              }
            />
            {errors.reloadDate?.type === "reload-date-between-stop-dates" && (
              <div
                style={{
                  color: "var(--error-500)",
                  fontSize: 12,
                  marginTop: 4,
                }}
              >
                {errors.reloadDate.message}
              </div>
            )}
          </div>
          {isSplitRoute && (
            <div className="mt-4">
              <RadioGroup
                value={watch("newRouteOption")}
                onChange={(e) =>
                  setValue(
                    "newRouteOption",
                    e.target.value as SplitRouteNewRouteOption
                  )
                }
              >
                <FormControlLabel
                  value="UNDISPATCHED"
                  control={<Radio size="small" />}
                  label={t("split:moveToUndispatched")}
                />
                <FormControlLabel
                  value="NEW_ROUTE"
                  control={<Radio size="small" />}
                  label={t("split:createNewRoute")}
                />
              </RadioGroup>
              {watch("newRouteOption") === "NEW_ROUTE" && (
                <div className="flex flex-col gap-2 mt-2">
                  <label>{t("split:selectTruck")}</label>
                  <UFDoraSelect
                    name="truckId"
                    control={control}
                    options={truckOptions}
                  />
                  <label>{t("split:selectTrailer")}</label>
                  <UFDoraSelect
                    name="trailerId"
                    control={control}
                    options={trailerOptions}
                  />
                  <label>{t("split:selectDriver")}</label>
                  <UFDoraSelect
                    name="driverId"
                    control={control}
                    options={driverOptions}
                  />
                  <label>{t("split:selectTeam")}</label>
                  <UFDoraSelect
                    name="teamId"
                    control={control}
                    options={teamOptions}
                  />
                  <label>{t("split:trailerNumber")}</label>
                  <UFDoraTextInput register={register("routeName")} />
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex justify-end gap-2 items-center">
          <DoraButton variant="primary" onClick={onDialogClose}>
            {t("components:buttonLabels.cancel")}
          </DoraButton>
          <DoraButton
            variant="primary-filled"
            onClick={onSplitClick}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? t("split:processing") : t("split:split")}
          </DoraButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}

const CargoClient = ({ clientId }: { clientId: string | null | undefined }) => {
  const client = useSelector(selectClient(clientId || null));
  return <span>{client?.client}</span>;
};

const SingleCargoDetails = ({
  cargo,
  formatAddress,
}: {
  cargo: ReturnType<typeof selectSplitCargos>[number];
  formatAddress: (place: any) => string;
}) => {
  const { t } = useTranslation(["split"]);
  const from = formatAddress(cargo.pickupList[0]?.place);
  const to = cargo.dropoffList?.length
    ? formatAddress(cargo.dropoffList[cargo.dropoffList.length - 1].place)
    : null;

  return (
    <div className="flex flex-col gap-1 py-2">
      <span>{cargo.ref}</span>
      <CargoClient clientId={cargo.clientId} />
      <div className="flex gap-2 items-center">
        <div>{from}</div>
        <div>→</div>
        <div>{t("split:reloadingLocation")}</div>
        {to && (
          <>
            <div>→</div>
            <div>{to}</div>
          </>
        )}
      </div>
    </div>
  );
};
